import {
  isDefinedArray,
  isNullOrUndefined,
  isPositiveNumber
} from "et3-type-guards";
import { memoize } from "lodash";
import { observable, runInAction } from "mobx";
import { apiCallChangeEmailSubscription } from "../../api/qa-api/api-call-qa-change-email-subscription";
import { apiCallQAUpdateUserStatus } from "../../api/qa-api/api-call-update-userStatus";
import getCorePopUpStore from "../core/core-popup-store";
import getCoreUIStore from "../core/core-ui-store";
import { apiCallObtainReceiptData } from "../../api/qa-api/api-call-qa-obtain-receipt";
import { TObtainReceiptData } from "../../Types";
import { clearDelay, delay } from "../../helpers/et3-timeout-helper";
import { DELAY_KEYS } from "../../constants/delay-constants";
import { TEt3PickerSingleValue } from "../../TypesNEW";
import { reducerToZeroIfUndefined } from "../../helpers/et3-picker-data-helper";

class qaStore {
  statusUserID = observable.box<number>(-1);
  subscriptionUserID = observable.box<number>(-1);
  userStatus = observable.box<string>("");
  changedStatusDate = observable.box<Date>(new Date());

  isCopied = observable.box<boolean>(false);
  receiptTokenData = observable.box<TObtainReceiptData>([]);
  receiptUserId = observable.box<number>(-1);
  receiptLimit = observable.box<TEt3PickerSingleValue>({
    label: "100",
    value: "100"
  });
  isReceiptCopied = observable.map<string, boolean>();

  setStatusUserID = (value: number) => {
    runInAction(() => {
      this.statusUserID.set(value);
    });
  };

  setSubscriptionUserID = (value: number) => {
    runInAction(() => {
      this.subscriptionUserID.set(value);
    });
  };

  setReceiptUserId = (value: number) => {
    runInAction(() => {
      this.receiptUserId.set(value);
    });
  };

  setReceiptLimit = (value: TEt3PickerSingleValue) => {
    runInAction(() => {
      this.receiptLimit.set(value);
    });
  };

  setReceiptTokenData = (data: TObtainReceiptData) => {
    runInAction(() => {
      this.receiptTokenData.set(data);
    });
  };

  setIsReceiptCopied = (value: string, state: boolean) => {
    runInAction(() => {
      this.isReceiptCopied.set(value, state);
    });
  };

  changeStatus = async () => {
    const userId = this.statusUserID.get();
    const statusDate = this.changedStatusDate.get();
    if (!isPositiveNumber(userId)) {
      return;
    }
    getCoreUIStore().setIsLoading("QA_CHANGE_STATUS_DATE", true);
    const { status, code } = await apiCallQAUpdateUserStatus({
      userID: userId,
      pageName: "QA_CHANGE_STATUS_DATE",
      statusDate
    });
    getCoreUIStore().setIsLoading("QA_CHANGE_STATUS_DATE", false);
    this.statusUserID.set(-1);
    if (status !== "OK") {
      this.changedStatusDate.set(new Date());
      getCorePopUpStore().showAlertMessage(code);
      return;
    } else {
      this.changedStatusDate.set(new Date());
      getCorePopUpStore().showSuccessMessage(
        "USER_STATUS_CHANGED_SUCCESSFULLY"
      );
      return;
    }
  };

  changeEmailSubscription = async (value: number) => {
    const userId = this.subscriptionUserID.get();
    if (!isPositiveNumber(userId)) {
      return;
    }
    getCoreUIStore().setIsLoading("QA_CHANGE_EMAIL_SUBSCRIPTION", true);
    const { status, code } = await apiCallChangeEmailSubscription({
      userID: userId,
      pageName: "QA_CHANGE_EMAIL_SUBSCRIPTION",
      subscriptionValue: value
    });
    getCoreUIStore().setIsLoading("QA_CHANGE_EMAIL_SUBSCRIPTION", false);
    this.subscriptionUserID.set(-1);
    if (status === "OK") {
      getCorePopUpStore().showSuccessMessage(
        "EMAIL_SUBSCRIPTION_STATUS_CHANGHED"
      );
      return;
    }
    getCorePopUpStore().showAlertMessage(code);
  };

  getReceiptToken = async () => {
    const userId = this.receiptUserId.get();
    const definedLimit = reducerToZeroIfUndefined(
      parseInt(this.receiptLimit.get().value, 10)
    );
    const isLimitDefinedNumber = isPositiveNumber(definedLimit);
    if (!isPositiveNumber(userId) || !isLimitDefinedNumber) {
      return;
    }
    getCoreUIStore().setIsLoading("QA_OBTAIN_IOS_RECEIPT_TOKENS", true);
    const { status, code, data } = await apiCallObtainReceiptData({
      userId,
      limit: definedLimit
    });
    getCoreUIStore().setIsLoading("QA_OBTAIN_IOS_RECEIPT_TOKENS", false);
    if (status === "OK") {
      this.setReceiptTokenData([]);
      if (!isNullOrUndefined(data) && isDefinedArray(data)) {
        this.setReceiptTokenData(data);
      } else {
        this.setReceiptTokenData([]);
        getCorePopUpStore().showAlertMessage("USER_HAS_NO_RECEIPTS");
      }
    } else {
      this.setReceiptTokenData([]);
      getCorePopUpStore().showAlertMessage(code);
    }
    return;
  };

  changingReceiptStatus = async (value: string) => {
    await delay(2000, DELAY_KEYS.COPY_RECEIPT_TIMEOUT);
    this.setIsReceiptCopied(value, false);
    clearDelay(DELAY_KEYS.COPY_RECEIPT_TIMEOUT);
  };
  copyReceiptLink = async (value: string) => {
    await navigator.clipboard.writeText(value);
    this.setIsReceiptCopied(value, true);
    this.changingReceiptStatus(value);
  };
}

const getQAStore = memoize(
  () => new qaStore(),
  () => 1
);

export default getQAStore;
